import { useEffect, useState } from 'react';
import { InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Restaurant, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import authSSO from '@/api/authSSO';
import parent from '@/api/parent';
import store from '@/store';
import { getSubdomain } from '@/helper/getSubDomain';
import { pathMenu } from '@/constants/routeName';

const schema = yup.object().shape({
	student_id: yup.string().required('Student ID is a required field'),
	password: yup.string().required('Password is a required field').min(8, 'Password min 8 characters'),
});

const { signin, signInSSO } = authSSO();
const { getParentMe } = parent();

export default function Auth() {
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const isSSO = false;
	const [values, setValues] = useState({ showPassword: false });
	const [loading, setLoading] = useState(false);

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			student_id: '',
			password: '',
		},
	});

	const handleClickShowPassword = () => {
		setValues({
			...values,
			showPassword: !values.showPassword,
		});
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const onSubmit = (dataAuth) => {
		setLoading(true);
		const actions = isSSO ? signInSSO : signin;
		actions({
			username: dataAuth.student_id.toLowerCase(),
			password: dataAuth.password,
		})
			.then(async (res) => {
				const { data } = res;
				if (!(data && res.status === 200)) {
					setLoading(false);
					return;
				}

				let payload = data;
				// Parent login returns customer: null — load children from outlet-parents/me/
				if (!data.user?.customer) {
					try {
						const meRes = await getParentMe({ token: data.token });
						const me = meRes.data;
						payload = {
							...data,
							account_type: me.account_type,
							children: me.children,
							parentOutlet: me.outlet,
							outlet: me.outlet,
						};
					} catch (meErr) {
						toast.error(meErr.response?.data?.detail || 'Could not load parent profile');
						setLoading(false);
						return;
					}
				} else {
					payload = {
						...data,
						account_type: data.account_type || 'legacy',
						children: [data.user.customer],
					};
				}

				store.dispatch({ type: 'auth/setLogginSSO', payload });
				const landing = payload.account_type === 'legacy' ? pathMenu : '/';
				setTimeout(() => {
					window.location.replace(landing);
				}, 300);
				setLoading(false);
			})
			.catch((err) => {
				toast.error(err.response?.data?.detail || 'Error');
				setLoading(false);
			});
	};

	useEffect(() => {
		localStorage.setItem('isSSO', JSON.stringify(isSSO ? false : false));
	}, [isSSO]);

	const school = getSubdomain(window.origin);
	const schoolLabel =
		school && !/^localhost/i.test(school) && school !== 'www' ? school : null;

	return (
		<section style={{ minHeight: innerHeight }} className='parent-ui auth-page'>
			<header className='brand'>
				<div className='wordmark'>
					<Restaurant fontSize='small' aria-hidden='true' />
					<span>Jungle Toppings</span>
				</div>
			</header>
			<main className='parent-main auth-main'>
				{schoolLabel && <p className='eyebrow'>{schoolLabel}</p>}
				<h1>Sign in</h1>
				<p className='muted subpage-intro'>
					Use your parent email or student ID and password to continue.
				</p>
				<form onSubmit={handleSubmit(onSubmit)} className='subpage-form'>
					<BaseInputValidation control={control} name='student_id' label='Student ID' errors={errors} />
					<BaseInputValidation
						control={control}
						name='password'
						label='Password'
						errors={errors}
						config={{ type: values.showPassword ? 'text' : 'password' }}
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										aria-label='toggle password visibility'
										onClick={handleClickShowPassword}
										onMouseDown={handleMouseDownPassword}
										edge='end'
									>
										{values.showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<button className='main full' type='submit' disabled={loading} style={{ marginTop: 8 }}>
						Sign in {loading && <CircularProgress className='ml-2' size={18} sx={{ color: 'var(--white)' }} />}
					</button>
				</form>
			</main>
		</section>
	);
}
